import { RiArrowDownSLine } from "@remixicon/react";
import type { LibraryAggregates, LibraryRoundCut as RoundCut } from "@screener/protocol";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@screener/uikit/components/collapsible";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { roundRankLabel } from "@/density/format";
import { ROUND_MARK_GLYPHS } from "@/density/round-mark";
import {
  DENSITIES_COUNT_HINT,
  isUnobservedOutcome,
  OFF_GRID_LABEL,
  OUTCOME_SHARES,
  outcomeShareLabel,
  pluralForm,
  ROUND_GROUPS,
  type RoundGroup,
  roundCutPriceHint,
  roundCutPriceLabel,
  UNOBSERVED_MARK,
} from "@/lib/labels";
import { formatCount, formatPercent, formatShare, MISSING } from "./format";
import { OutcomeBar, OutcomeSwatch, outcomeSegments } from "./outcome-bar";

/**
 * Разрез по круглости цены §9 (1.8, 1.28), вид §116: таблица на всю ширину
 * без карточки, четыре строки — некруглые и три ранга. В строке — подходы,
 * отскок на первом подходе и полоса исходов; в раскрытии — доли шести исходов
 * в процентах (количеств демон не отдаёт) и строка остальных агрегатов
 * группы, ни один не теряется. Подпись берётся из `cut.price` ответа, а не из
 * места разреза на экране (design.md stage8-library-visual, решение 3).
 *
 * Ширина колонок зависит от контейнера `library` экрана, а не от окна: боковая
 * панель отнимает часть ширины.
 */

/**
 * Колонки строки и её шапки — одна сетка, чтобы подписи стояли над значениями.
 * Макет — `minmax(0,1fr) 84px 92px 150px 26px`; в узком контейнере колонки
 * ужимаются, иначе таблица шире окна 390 px.
 */
const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_4.25rem_4.75rem_3.5rem_1rem] items-center gap-2 px-4 @[48rem]/library:grid-cols-[minmax(0,1fr)_84px_92px_150px_26px] @[48rem]/library:gap-3";

interface GroupRowProps {
  group: RoundGroup;
  aggregates: LibraryAggregates;
  defaultOpen?: boolean;
}

function groupLabel(group: RoundGroup): string {
  return group === "off_grid" ? OFF_GRID_LABEL : roundRankLabel(group);
}

/** Доли исходов словами для читалки: те же подписи и числа, что в раскрытии. */
function outcomeSharesText(shares: LibraryAggregates["outcome_shares"]): string {
  return OUTCOME_SHARES.map((outcome) => {
    const mark = isUnobservedOutcome(outcome) ? ` (${UNOBSERVED_MARK})` : "";
    return `${outcomeShareLabel(outcome)}${mark} ${formatShare(shares[outcome])}`;
  }).join(", ");
}

function GroupRow({ group, aggregates, defaultOpen }: GroupRowProps) {
  const hasOutcomes = outcomeSegments(aggregates.outcome_shares) !== null;
  return (
    <Collapsible data-round-group={group} defaultOpen={defaultOpen} className="border-b">
      <CollapsibleTrigger
        className={`${ROW_GRID} min-h-8 w-full cursor-pointer text-left text-[13px] tabular-nums outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset data-[panel-open]:bg-foreground/[0.025] [&[data-panel-open]>svg]:rotate-180`}
      >
        <span data-cell="label" className="flex min-w-0 items-baseline gap-2">
          <span
            aria-hidden="true"
            className="w-2.5 shrink-0 text-center text-[9px] text-muted-foreground"
          >
            {group === "off_grid" ? null : ROUND_MARK_GLYPHS[group]}
          </span>
          <span className={group === "off_grid" ? "font-semibold text-balance" : "text-balance"}>
            {groupLabel(group)}
          </span>
        </span>
        <span data-cell="approaches" className="text-right">
          <span className="sr-only">подходов: </span>
          {formatCount(aggregates.approaches)}
        </span>
        <span data-cell="bounce" className="text-right font-medium">
          <span className="sr-only">средний отскок на первом подходе: </span>
          {formatPercent(aggregates.mean_first_bounce_pct)}
        </span>
        <span data-cell="outcomes" className="flex items-center">
          <span className="sr-only">
            исходы: {hasOutcomes ? outcomeSharesText(aggregates.outcome_shares) : MISSING}
          </span>
          {hasOutcomes ? (
            <OutcomeBar shares={aggregates.outcome_shares} size="sm" />
          ) : (
            <span aria-hidden="true" className="text-muted-foreground">
              {MISSING}
            </span>
          )}
        </span>
        <RiArrowDownSLine
          aria-hidden="true"
          className="size-4 text-muted-foreground transition-transform duration-100 ease-linear"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div
          data-slot="library-group-details"
          className="flex flex-col gap-2.5 bg-foreground/[0.025] pt-2.5 pr-4 pb-3 pl-4 @[48rem]/library:pl-[34px]"
        >
          <dl
            data-slot="library-group-shares"
            className="grid grid-cols-3 gap-x-3 gap-y-2 @[48rem]/library:grid-cols-6"
          >
            {OUTCOME_SHARES.map((outcome) => (
              <ShareCell
                key={outcome}
                outcome={outcome}
                observed={!isUnobservedOutcome(outcome)}
                value={formatShare(aggregates.outcome_shares[outcome])}
              />
            ))}
          </dl>
          <dl data-slot="library-group-totals" className="flex flex-wrap gap-x-6 gap-y-1.5">
            <Detail label="Первых подходов" value={formatCount(aggregates.first_approaches)} />
            <Detail
              label="Плотностей"
              hint={DENSITIES_COUNT_HINT}
              value={formatCount(aggregates.densities)}
            />
            <Detail
              label="Средний отскок, все подходы"
              value={formatPercent(aggregates.mean_bounce_pct)}
            />
            <Detail label="Средний дисконт" value={formatPercent(aggregates.mean_discount_pct)} />
          </dl>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface ShareCellProps {
  outcome: (typeof OUTCOME_SHARES)[number];
  /** Исход досмотрен: у недосмотренного рядом с именем стоит словесная пометка. */
  observed: boolean;
  value: string;
}

/** Доля одного исхода в раскрытии: метка цвета, имя, процент. Количества нет. */
function ShareCell({ outcome, observed, value }: ShareCellProps) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <SectionLabel render={<dt />} className="flex items-center gap-1.5">
        <OutcomeSwatch outcome={outcome} className="h-1.5 w-1.5 rounded-[1px]" />
        <span className="min-w-0">{outcomeShareLabel(outcome)}</span>
      </SectionLabel>
      {/* Пометка недосмотренности — после процента, мелко и приглушённо, как в макете. */}
      <dd className="text-[13px] font-medium tabular-nums">
        {value}
        {observed ? null : (
          <>
            {" "}
            <span className="text-xs font-normal text-muted-foreground">{UNOBSERVED_MARK}</span>
          </>
        )}
      </dd>
    </div>
  );
}

interface DetailProps {
  label: string;
  value: string;
  /** Пояснение под именем — что именно считает величина. */
  hint?: string;
}

function Detail({ label, value, hint }: DetailProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">
        {label}
        {hint === undefined ? null : <span className="block">{hint}</span>}
      </dt>
      <dd className="text-[13px] font-medium tabular-nums">{value}</dd>
    </div>
  );
}

export interface LibraryRoundCutProps {
  cut: RoundCut;
  /**
   * Подходов выборки, не вошедших в этот разрез. Для разреза по цене
   * обнаружения — разность счётчиков; `0` и `undefined` строки не дают.
   */
  missingApproaches?: number;
  /** Строки раскрыты сразу; в экране не используется, нужен тестам разметки. */
  defaultOpen?: boolean;
}

export function LibraryRoundCut({ cut, missingApproaches, defaultOpen }: LibraryRoundCutProps) {
  const title = roundCutPriceLabel(cut.price);
  const hasMissing = missingApproaches !== undefined && missingApproaches > 0;
  return (
    // biome-ignore lint/a11y/useSemanticElements: `<fieldset>` — форма, а здесь группа строк-кнопок
    <div
      data-slot="library-round-cut"
      data-round-cut-price={cut.price}
      role="group"
      aria-label={title}
      className="flex min-w-0 flex-col"
    >
      <div aria-hidden="true" className={`${ROW_GRID} h-8 border-b`}>
        <SectionLabel className="whitespace-nowrap">Цена</SectionLabel>
        <SectionLabel className="text-right whitespace-nowrap">Подходов</SectionLabel>
        <SectionLabel className="text-right whitespace-nowrap">Отскок, 1-й</SectionLabel>
        <SectionLabel className="whitespace-nowrap">Исходы</SectionLabel>
        <span />
      </div>
      <div
        data-slot="library-cut-header"
        className="flex h-[30px] items-baseline gap-2 overflow-hidden border-b bg-foreground/[0.03] px-4 py-1.5 whitespace-nowrap"
      >
        <span className="text-[13px] font-semibold">{title}</span>
        <span className="truncate text-xs text-muted-foreground">
          {roundCutPriceHint(cut.price)}
        </span>
      </div>
      {ROUND_GROUPS.map((group) => (
        <GroupRow
          key={group}
          group={group}
          aggregates={cut.groups[group]}
          defaultOpen={defaultOpen}
        />
      ))}
      {hasMissing ? (
        <p data-slot="library-cut-missing" className="px-4 py-2 text-xs text-muted-foreground">
          {formatCount(missingApproaches)}{" "}
          {pluralForm(
            missingApproaches,
            "подход не вошёл",
            "подхода не вошли",
            "подходов не вошли",
          )}
          : у их плотностей нет цены обнаружения.
        </p>
      ) : null}
    </div>
  );
}
